import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Bell, Menu, X, Sun } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const SolarTeaser = () => {
  const { language } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNotifyMe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/qubit-network-logo.svg" 
                alt="Qubit Center" 
                className="w-8 h-8"
                style={{ filter: 'brightness(0) saturate(100%)' }}
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white">
                Qubit Center
              </span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'الرئيسية' : 'Home'}
              </a>
              <a href="/cpp-course" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'دورة C++' : 'C++ Course'}
              </a>
            </nav>
            
            <div className="flex items-center gap-4">
              <LanguageToggle variant="button" />
              
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Coming Soon */}
      <section className="relative overflow-hidden min-h-screen flex items-center justify-center">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-full text-sm font-medium mb-8">
              <Sun className="w-4 h-4" />
              {language === 'ar' ? 'قريباً - أنظمة الطاقة الشمسية' : 'Coming Soon - Solar Energy Systems'}
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              {language === 'ar' ? (
                <>تصميم وتركيب<br />أنظمة الطاقة الشمسية</>
              ) : (
                <>Solar Energy Systems<br />Design & Installation</>
              )}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
              {language === 'ar' 
                ? 'قريباً.. دورة شاملة لتعلم تصميم وتركيب أنظمة الطاقة الشمسية للمنازل والمباني التجارية'
                : 'Coming soon.. A comprehensive course on designing and installing solar energy systems for homes and commercial buildings'
              }
            </p>

            {/* Early Access Form */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-12 max-w-lg mx-auto border border-yellow-200 dark:border-gray-600">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Sun className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'كن أول من يعلم!' : 'Be the First to Know!'}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {language === 'ar' 
                  ? 'سجل بريدك الإلكتروني ليصلك الإعلان فور إطلاق الدورة'
                  : 'Register your email to get notified as soon as the course launches'
                }
              </p>
              
              {isSubscribed ? (
                <div className="flex items-center justify-center gap-3 text-green-600 dark:text-green-400 font-semibold">
                  <CheckCircle className="w-6 h-6" />
                  <span className="text-lg">{language === 'ar' ? 'شكراً! سنتواصل معك قريباً' : 'Thank you! We\'ll contact you soon'}</span>
                </div>
              ) : (
                <form onSubmit={handleNotifyMe} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email address'}
                    className="w-full px-6 py-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-3 shadow-lg"
                  >
                    <Bell className="w-5 h-5" />
                    {language === 'ar' ? 'أبلغني عند الإطلاق' : 'Notify Me at Launch'}
                  </button>
                </form>
              )}
            </div>

            {/* Navigation Links */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-16">
              <a
                href="/"
                className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
              >
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
                <ArrowRight className="w-5 h-5" />
              </a>
              
              <a
                href="/cpp-course"
                className="border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                {language === 'ar' ? 'تصفح دورة C++' : 'Browse C++ Course'}
              </a>
            </div>
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-yellow-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800"></div>
        <div className="absolute inset-0 -z-10 bg-grid-pattern opacity-5"></div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/qubit-network-logo.svg" 
                alt="Qubit Center" 
                className="w-8 h-8"
                style={{ filter: 'brightness(0) saturate(100%) invert(0.5)' }}
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white">Qubit Center</span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {language === 'ar' 
                ? 'منصة تعليمية متطورة لتعلم التقنيات الحديثة'
                : 'Advanced learning platform for modern technology education'
              }
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              © 2025 Qubit Center. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SolarTeaser;